import { Router } from '@angular/router';
import { DatePipe } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { BannerTypes } from '@zipari/shared-ds-util-messages';
import { WindowService } from '@zipari/web-services';
import { take } from 'rxjs/operators';
import { AccordionConfiguration } from '../../shared/interfaces/accordion.inteface';
import { camelCase } from '../../shared/utils/case-conversion.util';
import {
  ClaimSubmissionFooterButtons,
  claimSubmissionPages,
  InformationButtons,
  mobileHeaders,
  SubmissionCategory,
} from './claim-submission.constant';
import { ClaimSubmissionHttpService } from './claim-submission.http.service';

import {
  ClaimsInstructions,
  ClaimSubmission,
  ClaimSubmissionFooterConfig,
  ClaimSubmissionTabLabelConfig,
  ConfirmationPageConfig,
  TabsData,
} from './claim-submission.model';
import { ClaimSubmissionService } from './claim-submission.service';
import {
  ClaimSubmissionDocumentData,
  SubmittedClaimDetails,
} from './submittedClaim';
import { checkInputsForText } from '../../design-system.helper';

@Component({
  selector: 'lib-claim-submission',
  templateUrl: './claim-submission.component.html',
  styleUrls: ['./claim-submission.component.scss'],
})
export class ClaimSubmissionComponent implements OnInit, OnDestroy {
  @Input() config: ClaimSubmission;
  @Input() footerConfig: ClaimSubmissionFooterConfig;
  @Input() instructionConfig: ClaimsInstructions;
  @Input() confirmationPageConfig: ConfirmationPageConfig;
  @Input() submittedClaimContext: SubmittedClaimDetails;
  @Input() claimSubmissionCompleted = false;
  @Output() claimFooterButtonClicked: EventEmitter<string> = new EventEmitter();
  @Output() viewingSubmittedClaim: EventEmitter<string> = new EventEmitter();
  @Output() informationPageButtonClick: EventEmitter<string> =
    new EventEmitter();
  mobileHeaders: AccordionConfiguration;
  activeTab: TabsData & any;
  allTabs: TabsData[] & any[];
  activeTabKey: string;
  tabLabels: ClaimSubmissionTabLabelConfig[];
  selectedTabIndex = 0;
  totalClaimLineCharges = 0;
  submitButtonEnabled = false;
  claimSubmitForm: UntypedFormGroup;
  claimType: string;
  claimSubmittedSuccessfully: boolean;
  claimSubmissionId: string;
  currentPage: string;

  constructor(
    public claimSubmissionService: ClaimSubmissionService,
    public claimSubmissionHttpService: ClaimSubmissionHttpService,
    public router: Router,
    private windowService: WindowService,
  ) {}

  ngOnInit(): void {
    const [config, footerConfig, instructionConfig] = checkInputsForText([
      this.config,
      this.footerConfig,
      this.instructionConfig,
    ]);

    this.config = config as ClaimSubmission;
    this.footerConfig = footerConfig as ClaimSubmissionFooterConfig;
    this.instructionConfig = instructionConfig as ClaimsInstructions;

    this.setupTabs();
    this.setActivePage();
  }

  public setActivePage(): void {
    if (this.submittedClaimContext) {
      this.claimSubmissionService.apiResponse = this.submittedClaimContext;
      this.claimSubmissionService.claimId =
        this.claimSubmissionService.apiResponse.id;
      this.claimSubmissionService.claimNumber =
        this.claimSubmissionService.apiResponse.claimDetail.claimNumber;
      const uploadedFiles =
        this.submittedClaimContext.claimsubmissiondocument.map(
          (file: ClaimSubmissionDocumentData) => ({
            file_name: file.name,
            file_size: file?.size,
            file_type: file.type,
            id: file.documentId,
            url: file.link,
          }),
        );

      this.claimSubmissionService.uploadedFiles = uploadedFiles;
      this.currentPage = claimSubmissionPages.form;
      if (
        this.submittedClaimContext.submissionStatus === SubmissionCategory.draft
      ) {
        this.claimSubmissionCompleted = false;
      } else {
        this.claimSubmissionCompleted = true;
        this.viewingSubmittedClaim.emit();
      }
    } else {
      if (this.instructionConfig?.body && this.instructionConfig?.footer) {
        this.currentPage = claimSubmissionPages.instructionsPage;
      } else {
        this.currentPage = claimSubmissionPages.form;
      }
    }
  }

  public setupTabs(): void {
    this.allTabs = Object.keys(this.config.tabs).map(
      (key) => this.config.tabs[key],
    );
    this.allTabs.sort(this.sortTabs);
    this.tabLabels = this.allTabs.map((tabDetails) => ({
      text: tabDetails.tabLabel.text,
      pill: tabDetails.tabLabel.pill,
    }));
    this.setActiveTab(this.selectedTabIndex);
  }

  public keepOrder(tabA, tabB): number {
    return tabA.value.position - tabB.value.position;
  }

  public setActiveTab(index: number): void {
    this.activeTab = this.allTabs[index];
    this.activeTabKey = Object.keys(this.config.tabs).find(
      (val) =>
        this.config.tabs[val]['tabLabel'].text === this.tabLabels[index].text,
    );
    this.setMobileHeader(index);
  }

  public setMobileHeader(index: number): void {
    const position = this.allTabs[index].position;
    const length = this.allTabs.length;
    const label = this.allTabs[index].tabLabel.text;
    const nextTabIndex = index + 1;

    this.mobileHeaders = {
      ...mobileHeaders,
      header: `Step ${position} of ${length} : ${label}`,
      subHeader: this.allTabs[nextTabIndex]
        ? `Next: ${this.allTabs[nextTabIndex].tabLabel.text}`
        : '',
    };
  }

  public viewSubmittedClaim() {
    this.submittedClaimContext = null;
    this.claimSubmissionCompleted = true;
    this.viewingSubmittedClaim.emit(
      `${this.claimSubmissionService.claimNumber}#${this.claimSubmissionService.claimId}`,
    );
  }

  public editIconClickedEmitter(event: string) {
    this.selectedTabIndex = this.selectedTabIndex - 1;
    this.setActiveTab(this.selectedTabIndex);
    this.claimSubmissionService.hasReturnedFromReviewPage = true;
    this.claimSubmissionService.reviewSectionsId = event;
  }

  public saveClaim(): void {
    const claimSavedDate = new Date();
    const bannerSuccessMessage = `Form Saved on ${new DatePipe(
      'en-US',
    ).transform(claimSavedDate, 'MM-dd-YYYY')} at ${new DatePipe(
      'en-US',
    ).transform(claimSavedDate, 'HH:mm')}`;

    this.claimSubmissionService
      .claimSubmissionPostCall(
        this.claimSubmitForm,
        this.config.claimType,
        this.config.claimSubmissionUrl,
        SubmissionCategory.draft,
        this.config.policyId,
      )
      .pipe(take(1))
      .subscribe(
        (response) => {
          this.claimSubmissionHttpService.handleSaveMessageBanner(
            BannerTypes.success,
            bannerSuccessMessage,
            'check_circle_active',
            false,
          );
          this.claimSubmissionService.apiResponse = camelCase(response);
          this.claimSubmissionService.claimId =
            this.claimSubmissionService.apiResponse.id;
          this.claimFooterButtonClicked.emit(ClaimSubmissionFooterButtons.save);
        },
        (error) => {
          this.claimSubmissionHttpService.handleSaveMessageBanner(
            BannerTypes.error,
            error.message,
            'warning',
            false,
          );
        },
      );
    //  Scroll window to top to show banner message to user at the top of the page.
    this.windowService.nativeWindow.scroll(0, 0);
  }

  public footerButtonClicked(event): void {
    if (event === ClaimSubmissionFooterButtons.save) {
      this.saveClaim();
    } else if (event === ClaimSubmissionFooterButtons.review) {
      this.reviewClaim();
    } else if (event === ClaimSubmissionFooterButtons.submit) {
      this.submitClaim();
    } else {
      this.backToForm();
    }
  }

  public reviewClaim(): void {
    this.selectedTabIndex = this.selectedTabIndex + 1;
    this.setActiveTab(this.selectedTabIndex);
    this.closeSaveClaimsBanner();
    this.windowService.nativeWindow.scroll(0, 0);
  }

  public backToForm(): void {
    this.selectedTabIndex = this.selectedTabIndex - 1;
    this.setActiveTab(this.selectedTabIndex);
    this.claimSubmissionService.hasReturnedFromReviewPage = true;
    this.claimSubmissionService.reviewSectionsId = null;
    this.windowService.nativeWindow.scroll(0, 0);
  }

  public submitClaim(): void {
    const formValues = JSON.stringify(this.claimSubmitForm.value);

    this.windowService.postMessageForMobileDevices(formValues);
    this.claimSubmissionService
      .claimSubmissionPostCall(
        this.claimSubmitForm,
        this.config.claimType,
        this.config.claimSubmissionUrl,
        SubmissionCategory.submitted,
        this.config.policyId,
      )
      .pipe(take(1))
      .subscribe(
        (response) => {
          this.claimSubmissionService.apiResponse = camelCase(response);
          this.claimSubmissionService.claimId =
            this.claimSubmissionService.apiResponse.id;
          this.claimSubmissionService.claimNumber =
            this.claimSubmissionService.apiResponse.claimDetail.claimNumber;
          this.claimFooterButtonClicked.emit(
            ClaimSubmissionFooterButtons.submit,
          );
          if (response) {
            this.currentPage = claimSubmissionPages.confirmationPage;
          }
        },
        (error) => error,
      );
  }

  public cancelInstructions(): void {
    this.router.navigate(['claims-submission']);
    this.informationPageButtonClick.emit(InformationButtons.cancelButton);
  }

  public confirmInstructions(): void {
    this.currentPage = claimSubmissionPages.form;
    this.windowService.nativeWindow.scroll(0, 0);
    this.informationPageButtonClick.emit(InformationButtons.continueButton);
  }

  public certificationFormValueUpdated(
    certificationForm: UntypedFormGroup,
  ): void {
    this.submitButtonEnabled = certificationForm.valid;
  }

  public formValueUpdated(claimSubmitForm: UntypedFormGroup): void {
    this.claimSubmitForm = claimSubmitForm;
    const patientName =
      claimSubmitForm.get('patientInformation')?.value.patientName;

    if (patientName) {
      this.claimSubmissionService.selectedPatient = patientName.split('-');
    }
    this.claimSubmissionService.claimSubmitFormData = this.claimSubmitForm;
    const claimLineForm = this.claimSubmitForm.get(
      'claimLineInformation',
    )?.value;

    if (claimLineForm?.fg_array?.find(({ providerCharge }) => providerCharge)) {
      this.setClaimLineTotal(claimLineForm);
    }
  }

  public setClaimLineTotal(claimLineForm): void {
    let claimLineTotal = 0;

    claimLineForm.fg_array.reduce((total, claimLine) => {
      if (claimLine.providerCharge) {
        claimLineTotal = claimLineTotal + Number(claimLine?.providerCharge);
      }

      return total;
    }, {});

    this.totalClaimLineCharges = claimLineTotal;
  }

  public closeSaveClaimsBanner(): void {
    this.claimSubmissionHttpService.handleSaveMessageBanner(
      BannerTypes.success,
      '',
      '',
      true,
    );
  }

  ngOnDestroy() {
    this.closeSaveClaimsBanner();
    this.claimSubmissionService.claimSubmitFormData?.reset();
    this.claimSubmissionService.hasReturnedFromReviewPage = false;
    this.claimSubmissionService.uploadedFiles = [];
    this.claimSubmissionService.claimId = null;
    this.claimSubmissionService.reviewSectionsId = null;
  }

  private sortTabs(tabA, tabB): number {
    return tabA.position - tabB.position;
  }
}
